import { ContentType } from "@brookbush/database";
import { StructuredDataService } from "@brookbush/structured-data";
import type { GlossaryTerm } from "@brookbush/types";

import type { GetStaticPaths, GetStaticProps } from "next";
import { NextSeo } from "next-seo";

import GlossaryTermBody from "./components/GlossaryTermBody";
import GlossaryTermHeader from "./components/GlossaryTermHeader";
import Container from "~/components/Container";
import { useTrackHistory } from "~/hooks";
import useChatPageContent from "~/hooks/useChatPageContent";
import type { NextPageWithProps } from "~/types";
import fetcher from "~/utils/fetcher.client";

interface GlossaryTermPageProps {
	glossaryTerm: GlossaryTerm;
	structuredData: string;
}

const GlossaryTermPage: NextPageWithProps<GlossaryTermPageProps> = ({
	glossaryTerm,
	structuredData,
}) => {
	useTrackHistory({
		contentId: glossaryTerm._id,
		contentType: ContentType.GLOSSARY,
	});

	useChatPageContent({
		contentType: ContentType.GLOSSARY,
		contentId: glossaryTerm._id,
		title: glossaryTerm.title,
	});

	return (
		<>
			<NextSeo
				title={glossaryTerm.title}
				description={glossaryTerm.description}
				canonical={`${process.env.NEXT_PUBLIC_SITE_URL}/glossary/${glossaryTerm.slug}`}
				openGraph={{
					title: glossaryTerm.title,
					url: `${process.env.NEXT_PUBLIC_SITE_URL}/glossary/${glossaryTerm.slug}`,
					description: glossaryTerm.description,
					images: [
						{
							url: glossaryTerm.thumbnail?.url || "",
							width: 800,
							height: 600,
							alt: glossaryTerm.title,
						},
					],
				}}
			/>
			<script
				type="application/ld+json"
				id="json-ld"
				dangerouslySetInnerHTML={{
					__html: structuredData,
				}}
			/>

			<Container>
				<main>
					<GlossaryTermHeader glossaryTerm={glossaryTerm} />

					<GlossaryTermBody glossaryTerm={glossaryTerm} />
				</main>
			</Container>
		</>
	);
};

export const getStaticPaths: GetStaticPaths = async () => {
	const glossaryPaths = await fetcher(`/glossary/paths`);
	return {
		paths: glossaryPaths.map((slug: string) => ({
			params: { slug: String(slug) },
		})),
		fallback: `blocking`,
	};
};

export const getStaticProps: GetStaticProps = async (req) => {
	const slug = req.params?.slug as string;

	try {
		const glossaryTerm = await fetcher(`/glossary/${slug}`);

		if (!glossaryTerm) {
			return {
				notFound: true,
			};
		}

		const structuredData = await Promise.all([
			StructuredDataService.generateGlossary(glossaryTerm),
			StructuredDataService.getBreadcrumbs(`/glossary/${slug}`, (fragment) =>
				fragment === glossaryTerm.slug ? glossaryTerm.title : undefined,
			),
		]);

		for (const result of structuredData) {
			if (!result) {
				return {
					notFound: true,
				};
			}
		}

		return {
			props: {
				glossaryTerm,
				breadcrumbs: [
					{ name: "Glossary", href: "/glossary" },
					{
						name: glossaryTerm.title,
						href: `/glossary/${slug}`,
					},
				],
				structuredData: StructuredDataService.withContext({
					"@graph": structuredData,
				}),
			},
		};
	} catch (_error) {
		return {
			notFound: true,
		};
	}
};

export default GlossaryTermPage;
